import type { ButtonHTMLAttributes, ReactNode } from "react";

/**
 * A button.
 *
 * `label` is an i18n key, not text — a lint rule forbids bare JSX strings, and
 * this is where that rule stops being an annoyance and starts being the reason
 * the German build has no English left in it.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-cue-green text-white hover:bg-cue-green/90 disabled:bg-cue-green/40",
  secondary:
    "bg-white text-ink border border-paper-deep hover:bg-paper-warm disabled:text-ink-faint",
  ghost: "bg-transparent text-ink-soft hover:bg-paper-warm disabled:text-ink-faint",
  danger: "bg-accent text-white hover:bg-accent-dark disabled:bg-accent/40",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Shown while a request is in flight; also disables the button. */
  busy?: boolean;
  children: ReactNode;
}

export function Button({
  variant = "primary",
  busy = false,
  disabled,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2
                  text-sm font-medium transition-colors disabled:cursor-not-allowed
                  ${VARIANTS[variant]} ${className}`}
    >
      {children}
    </button>
  );
}
